/**
 * DataTable: hand-rolled admin table with sorting, text filtering, pagination, column visibility, optional
 * virtualized rows (@tanstack/react-virtual) and row selection whose bulk actions show dry-run counts before
 * they run (§16.3 "bulk operations have dry-run counts, scoped previews and idempotent execution").
 *
 * Semantics: a real <table> with <th scope>, `aria-sort` on sortable headers, native checkboxes for selection,
 * every control ≥44px (§13.3 density may increase, targets never shrink).
 */
import { useVirtualizer } from '@tanstack/react-virtual';
import { Button, Skeleton } from '@windrop/ui';
import { ArrowDown, ArrowUp, ArrowUpDown, Columns3 } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import type { DryRunResult } from './ConfirmDestructive';

export type SortDirection = 'asc' | 'desc';

export interface Column<Row> {
  id: string;
  header: string;
  cell: (row: Row) => ReactNode;
  /** Enables sorting; the value is compared with `<` so use numbers, ISO strings or lower-cased text. */
  sortValue?: (row: Row) => string | number | null | undefined;
  /** Text searched by the filter box; defaults to `sortValue` when present. */
  filterValue?: (row: Row) => string | number | null | undefined;
  width?: string;
  align?: 'start' | 'end';
  /** Default true. The first column is never hideable. */
  hideable?: boolean;
  defaultHidden?: boolean;
}

export interface BulkAction<Row> {
  id: string;
  label: string;
  /** Counts what the action would affect among the selected rows before it runs. */
  dryRun: (rows: Row[]) => DryRunResult;
  run: (rows: Row[]) => void | Promise<void>;
  destructive?: boolean;
}

export interface DataTableProps<Row> {
  caption: string;
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  loading?: boolean;
  emptyState?: ReactNode;
  /** Selection checkboxes (needed for bulk actions). */
  selectable?: boolean;
  bulkActions?: BulkAction<Row>[];
  onSelectionChange?: (rows: Row[]) => void;
  /** Rows per page; 0 disables pagination. */
  pageSize?: number;
  initialSort?: { columnId: string; direction: SortDirection };
  /** Hide the filter box. */
  filterable?: boolean;
  filterPlaceholder?: string;
  /** Virtualize body rows inside a scroll container of `maxHeight`. */
  virtualize?: boolean;
  rowHeight?: number;
  maxHeight?: number;
  /** Per-row actions column (links / buttons). */
  rowActions?: (row: Row) => ReactNode;
  /** Density: compact rows keep ≥44px controls. */
  density?: 'comfortable' | 'compact';
  /** Current test/clock for virtualization-free rendering. */
  testId?: string;
}

const compare = (a: unknown, b: unknown): number => {
  if (a === b) return 0;
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

export function sortRows<Row>(rows: Row[], column: Column<Row> | undefined, direction: SortDirection): Row[] {
  if (!column?.sortValue) return rows;
  const sv = column.sortValue;
  const sorted = [...rows].sort((a, b) => compare(sv(a), sv(b)));
  return direction === 'asc' ? sorted : sorted.reverse();
}

export function filterRows<Row>(rows: Row[], columns: Column<Row>[], text: string): Row[] {
  const q = text.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) =>
    columns.some((c) => {
      const fv = c.filterValue ?? c.sortValue;
      const v = fv ? fv(row) : null;
      return v !== null && v !== undefined && String(v).toLowerCase().includes(q);
    }),
  );
}

export function DataTable<Row>({
  caption,
  columns,
  rows,
  rowKey,
  loading = false,
  emptyState,
  selectable = false,
  bulkActions = [],
  onSelectionChange,
  pageSize = 25,
  initialSort,
  filterable = true,
  filterPlaceholder = 'Filter rows',
  virtualize = false,
  rowHeight = 48,
  maxHeight = 520,
  rowActions,
  density = 'comfortable',
  testId,
}: DataTableProps<Row>) {
  const id = useId();
  const [sort, setSort] = useState<{ columnId: string; direction: SortDirection } | null>(
    initialSort ?? null,
  );
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [hidden, setHidden] = useState<Set<string>>(
    () => new Set(columns.filter((c) => c.defaultHidden).map((c) => c.id)),
  );
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [running, setRunning] = useState<string | null>(null);

  const visibleColumns = columns.filter((c) => !hidden.has(c.id));
  const filtered = useMemo(() => filterRows(rows, columns, filter), [rows, columns, filter]);
  const sorted = useMemo(
    () =>
      sortRows(
        filtered,
        sort ? columns.find((c) => c.id === sort.columnId) : undefined,
        sort?.direction ?? 'asc',
      ),
    [filtered, columns, sort],
  );
  const pageCount = pageSize > 0 ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = pageSize > 0 ? sorted.slice(safePage * pageSize, (safePage + 1) * pageSize) : sorted;

  // Drop selections for rows that disappeared.
  useEffect(() => {
    const keys = new Set(rows.map(rowKey));
    setSelected((prev) => {
      const next = new Set([...prev].filter((k) => keys.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows, rowKey]);

  const selectedRows = useMemo(() => rows.filter((r) => selected.has(rowKey(r))), [rows, selected, rowKey]);
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;
  useEffect(() => {
    onSelectionChangeRef.current?.(selectedRows);
  }, [selectedRows]);

  const toggleSort = (column: Column<Row>) => {
    if (!column.sortValue) return;
    setSort((s) => {
      if (!s || s.columnId !== column.id) return { columnId: column.id, direction: 'asc' };
      return s.direction === 'asc' ? { columnId: column.id, direction: 'desc' } : null;
    });
  };

  const allOnPageSelected = pageRows.length > 0 && pageRows.every((r) => selected.has(rowKey(r)));
  const someOnPageSelected = pageRows.some((r) => selected.has(rowKey(r)));
  const headerCheckbox = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (headerCheckbox.current)
      headerCheckbox.current.indeterminate = someOnPageSelected && !allOnPageSelected;
  }, [someOnPageSelected, allOnPageSelected]);

  const togglePage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of pageRows) {
        if (allOnPageSelected) next.delete(rowKey(r));
        else next.add(rowKey(r));
      }
      return next;
    });
  };

  // Virtualization (only when asked; a plain tbody otherwise so tests and small lists stay simple).
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: virtualize ? pageRows.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 8,
  });
  const virtualItems = virtualize ? virtualizer.getVirtualItems() : [];
  const paddingTop = virtualize && virtualItems.length > 0 ? (virtualItems[0]?.start ?? 0) : 0;
  const paddingBottom =
    virtualize && virtualItems.length > 0
      ? virtualizer.getTotalSize() - (virtualItems[virtualItems.length - 1]?.end ?? 0)
      : 0;
  const renderedRows = virtualize
    ? virtualItems.map((v) => pageRows[v.index]).filter((r): r is Row => r !== undefined)
    : pageRows;

  const colSpan = visibleColumns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0);

  const renderRow = (row: Row) => {
    const key = rowKey(row);
    const isSelected = selected.has(key);
    return (
      <tr
        key={key}
        aria-selected={selectable ? isSelected : undefined}
        data-selected={isSelected ? 'true' : undefined}
      >
        {selectable ? (
          <td className="wdc-table__select">
            <label className="wdc-table__check wd-touch">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() =>
                  setSelected((prev) => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  })
                }
                aria-label={`Select row ${key}`}
              />
            </label>
          </td>
        ) : null}
        {visibleColumns.map((c) => (
          <td key={c.id} data-align={c.align ?? 'start'}>
            {c.cell(row)}
          </td>
        ))}
        {rowActions ? <td className="wdc-table__actions">{rowActions(row)}</td> : null}
      </tr>
    );
  };

  return (
    <div className="wdc-datatable" data-density={density} data-testid={testId}>
      <div className="wdc-datatable__toolbar">
        {filterable ? (
          <div className="wdc-datatable__filter">
            <label htmlFor={`${id}-filter`} className="wd-sr-only">
              {filterPlaceholder}
            </label>
            <input
              id={`${id}-filter`}
              className="wd-input"
              type="search"
              placeholder={filterPlaceholder}
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setPage(0);
              }}
            />
          </div>
        ) : null}
        <p className="wd-muted wdc-datatable__count" aria-live="polite">
          {loading ? 'Loading…' : `${sorted.length} of ${rows.length} row${rows.length === 1 ? '' : 's'}`}
        </p>
        <div className="wdc-datatable__columns">
          <Button
            variant="quiet"
            icon={<Columns3 size={16} />}
            aria-expanded={columnsOpen}
            aria-controls={`${id}-columns`}
            onClick={() => setColumnsOpen((o) => !o)}
          >
            Columns
          </Button>
          {columnsOpen ? (
            <fieldset id={`${id}-columns`} className="wdc-datatable__columns-menu wd-elevated">
              <legend>Visible columns</legend>
              {columns.map((c, i) => (
                <label key={c.id} className="wd-check">
                  <input
                    type="checkbox"
                    checked={!hidden.has(c.id)}
                    disabled={i === 0 || c.hideable === false}
                    onChange={() =>
                      setHidden((prev) => {
                        const next = new Set(prev);
                        if (next.has(c.id)) next.delete(c.id);
                        else next.add(c.id);
                        return next;
                      })
                    }
                  />
                  <span className="wd-check__text">{c.header}</span>
                </label>
              ))}
            </fieldset>
          ) : null}
        </div>
      </div>

      {selectable && selected.size > 0 ? (
        <div className="wdc-bulkbar wd-elevated" role="region" aria-label="Bulk actions">
          <p>
            <strong data-testid="selected-count">{selected.size}</strong> selected
          </p>
          {bulkActions.map((action) => {
            const dry = action.dryRun(selectedRows);
            return (
              <Button
                key={action.id}
                variant={action.destructive ? 'danger' : 'secondary'}
                loading={running === action.id}
                disabled={dry.affected === 0}
                disabledReason={
                  dry.affected === 0
                    ? `None of the selected rows can be affected by “${action.label}”.`
                    : undefined
                }
                onClick={async () => {
                  setRunning(action.id);
                  try {
                    await action.run(selectedRows);
                  } finally {
                    setRunning(null);
                  }
                }}
                data-testid={`bulk-${action.id}`}
              >
                {action.label} ({dry.affected ?? 'unknown'} of {selectedRows.length}
                {dry.detail ? `, ${dry.detail}` : ''})
              </Button>
            );
          })}
          <Button variant="quiet" onClick={() => setSelected(new Set())}>
            Clear selection
          </Button>
        </div>
      ) : null}

      <div
        className="wdc-datatable__scroll"
        ref={scrollRef}
        style={virtualize ? { maxHeight, overflow: 'auto' } : undefined}
      >
        <table className="wdc-table">
          <caption className="wd-sr-only">{caption}</caption>
          <thead>
            <tr>
              {selectable ? (
                <th scope="col" className="wdc-table__select">
                  <label className="wdc-table__check wd-touch">
                    <input
                      ref={headerCheckbox}
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={togglePage}
                      aria-label={
                        allOnPageSelected ? 'Deselect all rows on this page' : 'Select all rows on this page'
                      }
                    />
                  </label>
                </th>
              ) : null}
              {visibleColumns.map((c) => {
                const active = sort?.columnId === c.id;
                const ariaSort = active
                  ? sort?.direction === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : c.sortValue
                    ? 'none'
                    : undefined;
                return (
                  <th
                    key={c.id}
                    scope="col"
                    style={{ width: c.width }}
                    aria-sort={ariaSort}
                    data-align={c.align ?? 'start'}
                  >
                    {c.sortValue ? (
                      <button
                        type="button"
                        className="wdc-table__sort wd-touch"
                        onClick={() => toggleSort(c)}
                      >
                        {c.header}
                        {active ? (
                          sort?.direction === 'asc' ? (
                            <ArrowUp size={14} aria-hidden="true" />
                          ) : (
                            <ArrowDown size={14} aria-hidden="true" />
                          )
                        ) : (
                          <ArrowUpDown size={14} aria-hidden="true" />
                        )}
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
              {rowActions ? (
                <th scope="col" className="wdc-table__actions">
                  <span className="wd-sr-only">Actions</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colSpan}>
                  <Skeleton shape="block" height={rowHeight * 3} label="Loading rows" />
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={colSpan}>{emptyState ?? <p className="wd-muted">Nothing matches.</p>}</td>
              </tr>
            ) : (
              <>
                {paddingTop > 0 ? (
                  <tr aria-hidden="true">
                    <td colSpan={colSpan} style={{ height: paddingTop, padding: 0, border: 0 }} />
                  </tr>
                ) : null}
                {renderedRows.map(renderRow)}
                {paddingBottom > 0 ? (
                  <tr aria-hidden="true">
                    <td colSpan={colSpan} style={{ height: paddingBottom, padding: 0, border: 0 }} />
                  </tr>
                ) : null}
              </>
            )}
          </tbody>
        </table>
      </div>

      {pageSize > 0 && pageCount > 1 ? (
        <nav className="wdc-datatable__pager" aria-label={`${caption} pagination`}>
          <Button variant="quiet" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>
            Previous
          </Button>
          <p>
            Page {safePage + 1} of {pageCount}
          </p>
          <Button variant="quiet" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)}>
            Next
          </Button>
        </nav>
      ) : null}
    </div>
  );
}
